import {
	sanitizeRawApiErrorBodyForUser,
	userFriendlyMessageForHttpError,
} from "@Front/Utils/sanitizeApiErrorBodyForUser";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { isHttpStatusAuthSessionFailure } from "@Front/Api/helpers/httpAuthSessionFailurePolicy";
import { handleAuthSessionFailureForEndpoint } from "@Front/Api/helpers/authSessionFailureHandlingHelpers";
import type { ProcessResponseOptions } from "./types";
import { retryWithoutCacheOnce } from "./helpers/noCacheRetryHelpers";

export class BaseApiServiceResponseHandler {
	public static async handleCriticalErrors(httpStatus: number, endpoint?: string): Promise<void> {
		if (typeof window === "undefined") return;
		if (!isHttpStatusAuthSessionFailure(httpStatus)) return;
		try {
			await handleAuthSessionFailureForEndpoint(httpStatus, endpoint, "BaseApiService");
		} catch (error) {
			LoggerService.error("❌ [BaseApiService] Error during session failure handling", { error });
		}
	}

	private static resolveUserFacingErrorMessage(httpStatus: number, rawBody: string): string {
		const trimmed = rawBody.trim();
		const mapped = userFriendlyMessageForHttpError(httpStatus, trimmed.length > 0 ? trimmed : null);
		if (mapped) {
			return mapped;
		}
		if (trimmed.length > 0) {
			return sanitizeRawApiErrorBodyForUser(trimmed);
		}
		return "Unable to read response body";
	}

	/** Server-assigned request id (X-Request-Id / body request_id): present ⇔ a server response reached the client. */
	private static resolveRequestId(response: Response, bodyRecord?: Record<string, unknown>): string | undefined {
		const fromBody = bodyRecord?.["request_id"];
		if (typeof fromBody === "string" && fromBody.length > 0) {
			return fromBody;
		}
		const fromHeader = response.headers.get("x-request-id");
		return fromHeader && fromHeader.length > 0 ? fromHeader : undefined;
	}

	public static async handleErrorResponse(response: Response): Promise<never> {
		const httpStatus = response.status;
		const text = await response.text();
		const userMessage = BaseApiServiceResponseHandler.resolveUserFacingErrorMessage(httpStatus, text);

		let parsed: unknown;
		try {
			parsed = JSON.parse(text);
		} catch (parseError: unknown) {
			if (parseError instanceof SyntaxError) {
				throw {
					http_status: httpStatus,
					message: userMessage,
					request_id: BaseApiServiceResponseHandler.resolveRequestId(response),
				};
			}
			throw parseError;
		}

		if (parsed !== null && typeof parsed === "object") {
			const record = parsed as Record<string, unknown>;
			const rawMsg = record["message"];
			const msg =
				typeof rawMsg === "string" && rawMsg.trim().length > 0
					? sanitizeRawApiErrorBodyForUser(rawMsg)
					: userMessage;
			const rawStatus = record["http_status"];
			throw {
				...record,
				http_status: typeof rawStatus === "number" ? rawStatus : httpStatus,
				message: msg,
				request_id: BaseApiServiceResponseHandler.resolveRequestId(response, record),
			};
		}

		throw {
			http_status: httpStatus,
			message: userMessage,
			request_id: BaseApiServiceResponseHandler.resolveRequestId(response),
		};
	}

	public static async handleNotModifiedResponse<T>(
		response: Response,
		request: () => Promise<Response>,
		processResponse: (
			response: Response,
			request: () => Promise<Response>,
			options?: ProcessResponseOptions,
		) => Promise<T>,
		options: ProcessResponseOptions = {},
	): Promise<T> {
		return retryWithoutCacheOnce<T>({
			response,
			request,
			processResponse,
			options,
			reason: "304 Not Modified",
			logLevel: "debug",
		});
	}

	public static async handleEmptyResponseBody<T>(
		response: Response,
		request: () => Promise<Response>,
		processResponse: (
			response: Response,
			request: () => Promise<Response>,
			options?: ProcessResponseOptions,
		) => Promise<T>,
		options: ProcessResponseOptions = {},
	): Promise<T> {
		return retryWithoutCacheOnce<T>({
			response,
			request,
			processResponse,
			options,
			reason: "Empty response body",
			logLevel: "warn",
		});
	}

	public static async handleBlobContent<T>(response: Response, contentType: string | null): Promise<T | null> {
		// For non-JSON content types (PDF, images, zip archives, raw binary, etc.), return as blob
		if (contentType && !contentType.includes("application/json") && !contentType.includes("text/plain")) {
			if (
				contentType.includes("pdf") ||
				contentType.includes("image") ||
				contentType.includes("octet-stream") ||
				contentType.includes("zip")
			) {
				return (await response.blob()) as T;
			}
		}
		return null;
	}

	public static handleEmptyTextResponse<T>(response: Response, contentType: string | null): T | null {
		// For successful responses (201 Created), empty body might be acceptable
		if (response.status === 201) {
			LoggerService.warn("⚠️ [BaseApiService] Empty response body for 201 Created", {
				url: response.url,
				status: response.status,
				contentType,
			});
			// Return empty object for 201 Created with empty body
			return {} as T;
		}
		return null;
	}

	private static isPkZipMagicPrefix(text: string): boolean {
		return text.length >= 2 && text.charCodeAt(0) === 0x50 && text.charCodeAt(1) === 0x4b;
	}

	private static contentSuggestsZipOrOctetStream(contentType: string | null): boolean {
		if (!contentType) return false;
		if (contentType.includes("application/json") || contentType.includes("text/plain")) return false;
		return contentType.includes("zip") || contentType.includes("octet-stream");
	}

	private static contentSuggestsNonJsonBlob(contentType: string | null): boolean {
		if (!contentType) return false;
		return !contentType.includes("application/json") && !contentType.includes("text/plain");
	}

	private static async blobFromCloneOrThrow<T>(responseClone: Response | null): Promise<T> {
		if (!responseClone) {
			throw new Error("parseJsonResponse: missing clone for blob fallback");
		}
		return (await responseClone.blob()) as T;
	}

	private static async handleJsonParseFailure<T>(args: {
		error: unknown;
		text: string;
		response: Response;
		responseClone: Response | null;
		contentType: string | null;
	}): Promise<T> {
		const { error, text, response, responseClone, contentType } = args;
		const zipMagic = BaseApiServiceResponseHandler.isPkZipMagicPrefix(text);
		const nonJsonZipOrOctet =
			BaseApiServiceResponseHandler.contentSuggestsNonJsonBlob(contentType) &&
			BaseApiServiceResponseHandler.contentSuggestsZipOrOctetStream(contentType);
		if (responseClone && (nonJsonZipOrOctet || zipMagic)) {
			return BaseApiServiceResponseHandler.blobFromCloneOrThrow<T>(responseClone);
		}
		LoggerService.error("❌ [BaseApiService] Failed to parse JSON response", {
			url: response.url,
			status: response.status,
			contentType,
			textPreview: text.substring(0, 200),
			error,
		});
		if (contentType && !contentType.includes("application/json") && responseClone) {
			LoggerService.warn("⚠️ [BaseApiService] JSON parse failed, trying as blob", {
				url: response.url,
				contentType,
			});
			return BaseApiServiceResponseHandler.blobFromCloneOrThrow<T>(responseClone);
		}
		throw error;
	}

	public static async parseJsonResponse<T>(
		text: string,
		response: Response,
		responseClone: Response | null,
		contentType: string | null,
	): Promise<T> {
		try {
			return JSON.parse(text) as T;
		} catch (error) {
			return BaseApiServiceResponseHandler.handleJsonParseFailure<T>({
				error,
				text,
				response,
				responseClone,
				contentType,
			});
		}
	}
}
